import { useState, useEffect, useCallback, useRef } from 'react';
import { resolveTabWorkspace } from '../services/prest0nRuntime/tabWorkspace';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useMaybeAuth } from '../context/AuthContext';
import { isRetryableRbacReadError } from './rbacReadError';
import { pickBackPointerAccountId } from '../services/prest0nRuntime/backPointer';

interface RbacState {
  permissions: Set<string>;
  isOwner: boolean;
  loading: boolean;
  error: string | null;
  /**
   * ORG-USAGE-2026: from the member doc. null = this account predates the
   * usage-visibility permission model, so those gates fail OPEN (see
   * usageVisibility.ts). Purely additive — no existing consumer reads it.
   */
  usageVisibilityGeneration: number | null;
  /** §7.10 pause freeze (2026-08-16): member-doc status in the resolved
   *  workspace. 'suspended' = frozen read-only (banner + posting blocked
   *  server-side); null = unknown/ownerless resolution paths. */
  membershipStatus: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Module-level resolution cache (ORG-USAGE-2026 hardening).
//
// Every consumer of this hook (header, wallet wall, BOTH ⓘ popups, the limit
// banner) used to run its own full Firestore resolution walk on every mount.
// Two production consequences:
//   1. On a governed account, a member explicitly DENIED usage.view-own saw
//      the restricted model/token/cost detail flash for the whole round-trip
//      every time they opened the popup — the gate fails open while loading,
//      by design, so re-entering the loading state on every mount re-opened
//      the gate.
//   2. Several Firestore reads (+ possible discovery queries) per popup open
//      and per navigation, per signed-in user, forever.
// The cache makes the FIRST resolution per (uid, account) do the walk; later
// mounts seed synchronously from the resolved state (no loading phase, no
// fail-open window) and refresh in the background at most once per TTL.
// Error states are cached for subscribers of the same in-flight fetch but are
// never seeded into new mounts, so retry behavior is unchanged.
// ────────────────────────────────────────────────────────────────────────────

interface CachedRbac {
  state: RbacState;
  fetchedAtMs: number;
  /** true only for clean resolutions — the only states safe to seed from */
  good: boolean;
}

const rbacCache = new Map<string, CachedRbac>();
const inflightFetches = new Map<string, Promise<void>>();
const RBAC_CACHE_TTL_MS = 60_000;

/** Test hook: clears module state between test cases. */
export function __resetRbacCacheForTests(): void {
  rbacCache.clear();
  inflightFetches.clear();
}

function cacheKey(uid: string, accountIdHint: string | null | undefined): string {
  // The hint is the HOME org (AuthContext.user.accountId) and does not move
  // when the workspace changes, so on its own this key served one org's
  // permissions to another. Include the tab's own workspace: two tabs in
  // different organizations now hold separate entries instead of racing over
  // one (owner report 2026-09-11).
  const tabWorkspace = resolveTabWorkspace({ uid, accountFromPointer: null }).accountId ?? '';
  return `${uid}::${accountIdHint ?? ''}::${tabWorkspace}`;
}

const EMPTY_STATE: RbacState = {
  permissions: new Set(),
  isOwner: false,
  loading: false,
  error: null,
  usageVisibilityGeneration: null,
  membershipStatus: null,
};

export function useRbac() {
  // Non-throwing variant: this hook is now consumed by the per-message ⓘ popup
  // and the header, which can mount outside AuthProvider (embedded hosts,
  // isolated component tests). Throwing there would take down the whole chat
  // surface. With no provider there is no user, so RBAC simply resolves to the
  // empty state — and every visibility gate built on it fails OPEN.
  const auth = useMaybeAuth();
  const user = auth?.user ?? null;

  const [state, setState] = useState<RbacState>(() => {
    if (!user?.id) return EMPTY_STATE;
    const cached = rbacCache.get(cacheKey(user.id, user.accountId));
    // Seed synchronously from the last clean resolution — a second mount must
    // never re-enter the fail-open loading state.
    if (cached?.good) return cached.state;
    return { ...EMPTY_STATE, permissions: new Set(), loading: true };
  });

  const lastFetchedKey = useRef<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetState = useCallback((s: RbacState) => {
    if (mountedRef.current) setState(s);
  }, []);

  // Commit a terminal state: publish to the shared cache, then to this
  // instance. Clean states are seedable; error states are visible to current
  // subscribers but never seeded into future mounts (so retries still happen).
  const commit = useCallback(
    (key: string, s: RbacState) => {
      rbacCache.set(key, { state: s, fetchedAtMs: Date.now(), good: !s.loading && s.error === null });
      safeSetState(s);
    },
    [safeSetState]
  );

  // Try to resolve RBAC state for a given (uid, accountId) pair.
  //   'resolved'  — a member doc or owner field matched; state has been set.
  //   'not-found' — reads succeeded but this account is genuinely not the
  //                 caller's (no member doc, not owner). Caller tries the next.
  //   'error'     — a read THREW with a RETRYABLE code (transient network,
  //                 blocked Firestore Listen channel). We could NOT determine
  //                 access. Distinct from 'not-found' so the caller does not
  //                 mistake a flaky read for a genuine "access denied" and show
  //                 a false "access restricted" wall to a legitimate owner.
  //                 A deterministic permission-denied is NOT an error — rules
  //                 answer the same on every retry, and the accounts/{id}
  //                 owner-field probe below is rules-denied by design for
  //                 non-email-hash ids — so it counts as 'not-found'
  //                 (see rbacReadError.ts; 2026-07-19 QA-account incident).
  const tryResolveForAccount = useCallback(async (uid: string, accountId: string, db: any, setStateIfResolved: (s: RbacState) => void): Promise<'resolved' | 'not-found' | 'error'> => {
    let readErrored = false;

    // Member doc and account root fetch CONCURRENTLY (2026-08-17 owner report
    // "it literally said 'yours' but it wasn't allowing me on the wallet
    // page"): a seeded member doc with isOwner:false used to SHORT-CIRCUIT
    // resolution and never consult the root owner fields — while the
    // pill/switcher (listMyWorkspaces) DOES honor root ownership. Same person,
    // "yours" in the chrome, access-walled on the page. Root ownership now
    // overrides a non-owner member doc, matching the server's own
    // verifyWorkspaceAccess / listVerifiedWorkspaces posture.
    const [memberRes, accountRes] = await Promise.allSettled([
      getDoc(doc(db, 'accounts', accountId, 'members', uid)),
      getDoc(doc(db, 'accounts', accountId)),
    ]);

    let ownerByRootField = false;
    if (accountRes.status === 'fulfilled' && accountRes.value.exists()) {
      const accData: any = accountRes.value.data();
      ownerByRootField =
        accData?.ownerUid === uid ||
        accData?.firebaseUid === uid ||
        accData?.accountId === uid;
    } else if (accountRes.status === 'rejected') {
      // The root probe is rules-denied by design for most non-own accounts —
      // deterministic denials are NOT retryable errors (rbacReadError.ts).
      if (isRetryableRbacReadError(accountRes.reason)) readErrored = true;
      console.warn('[useRbac] account read failed for', accountId, accountRes.reason);
    }

    if (memberRes.status === 'fulfilled' && memberRes.value.exists()) {
      const d: any = memberRes.value.data();
      setStateIfResolved({
        permissions: new Set(d.effectivePermissions ?? []),
        isOwner: (d.isOwner ?? false) || ownerByRootField,
        loading: false,
        error: null,
        usageVisibilityGeneration:
          typeof d.usageVisibilityGeneration === 'number' ? d.usageVisibilityGeneration : null,
        membershipStatus: typeof d.status === 'string' ? d.status : 'active',
      });
      return 'resolved';
    }
    if (memberRes.status === 'rejected') {
      if (isRetryableRbacReadError(memberRes.reason)) readErrored = true;
      console.warn('[useRbac] member read failed for', accountId, memberRes.reason);
    }

    if (ownerByRootField) {
      setStateIfResolved({ permissions: new Set(), isOwner: true, loading: false, error: null, usageVisibilityGeneration: null, membershipStatus: 'active' });
      return 'resolved';
    }

    return readErrored ? 'error' : 'not-found';
  }, []);

  const runFetch = useCallback(async (key: string, uid: string, accountIdHint?: string) => {
    const db = getFirestore(getApp());
    const commitState = (s: RbacState) => commit(key, s);

    try {
      // Step 1: build the candidate list of accountIds to try, in priority order.
      // We MUST try multiple candidates because legacy accounts have a profile-based
      // `accountId` (e.g. numeric customer ID "131939169") that doesn't correspond to the
      // UID-keyed accounts/{uid} doc where RBAC data lives.
      const candidates: string[] = [];
      const pushUnique = (id: string | null | undefined) => {
        if (id && !candidates.includes(id)) candidates.push(id);
      };

      // MULTI-ORG FIX (owner report 2026-08-16: "no matter what organization
      // I switch to … it shows I'm the same member / same balance"): the
      // ACTIVE-WORKSPACE back-pointer resolves FIRST. It is the one candidate
      // `switchActiveWorkspace` actually writes, and the server's own
      // resolveAccountId has ordered it before the raw uid since the
      // 2026-08-04 split-brain fix. The old order here (hint → uid →
      // back-pointer) meant every user with a uid-keyed account of their own
      // — every founder and every native org — resolved to that SAME account
      // regardless of the switch, pinning Activity/Wallet/Team to one
      // workspace forever.
      try {
        const membershipRef = doc(db, 'users', uid, 'account_membership', 'main');
        const membershipSnap = await getDoc(membershipRef);
        // Org stickiness round 2 (2026-08-19): FULL back-pointer chain — the
        // narrow accountId-only read skipped legacy invite-accept shapes
        // (defaultAccountId/memberships[] only), sending those users down
        // the uid/discovery candidates and into the wrong workspace.
        if (membershipSnap.exists()) {
          const pointer = pickBackPointerAccountId(membershipSnap.data() as Record<string, unknown>);
          // PER-TAB ORGANIZATION (owner report 2026-09-11). That pointer is
          // ACCOUNT-WIDE, and this hook re-resolves on a 60s TTL WITHOUT
          // re-entering `loading` — so when another device switched, this tab's
          // permission set silently became the other organization's a minute
          // later: nav links appeared and vanished and route guards began
          // walling pages the user was already sitting on. Resolve the tab's
          // own workspace first; the pointer stays the fallback (and still the
          // first candidate) for a tab that has no pin.
          pushUnique(resolveTabWorkspace({ uid, accountFromPointer: pointer }).accountId);
          pushUnique(pointer);
        }
      } catch (e) {
        console.warn('[useRbac] account_membership read failed', e);
        // The active-workspace pointer is UNREADABLE (transient). Walking the
        // remaining candidates would bind every gate to whichever account
        // resolves first — usually the caller's native org, NOT the workspace
        // they are actually in (the intermittent wrong-workspace denials,
        // 2026-08-17). Fail retryable instead: gates stay open on error by
        // design (usageVisibility residual) and the next mount/TTL retries.
        if (isRetryableRbacReadError(e)) {
          commitState({ permissions: new Set(), isOwner: false, loading: false, error: 'resolve_failed', usageVisibilityGeneration: null, membershipStatus: null });
          return;
        }
      }

      pushUnique(accountIdHint);

      // UID-keyed direct read (many RBAC accounts are keyed by the owner's UID)
      pushUnique(uid);

      // Query-based discovery (may be rule-blocked; we try anyway)
      for (const field of ['ownerUid', 'uid', 'firebaseUid']) {
        try {
          const q = query(collection(db, 'accounts'), where(field, '==', uid));
          const snap = await getDocs(q);
          if (!snap.empty) pushUnique(snap.docs[0].id);
        } catch { /* rules may block list, ignore */ }
      }

      if (candidates.length === 0) {
        commitState({ permissions: new Set(), isOwner: false, loading: false, error: 'no_account', usageVisibilityGeneration: null, membershipStatus: null });
        return;
      }

      console.log('[useRbac] candidate accountIds:', candidates, 'for uid:', uid);

      // Step 2: walk the candidate list, stop on first success.
      const auth = getAuth(getApp());
      if (auth.currentUser) {
        let anyReadError = false;
        for (const accountId of candidates) {
          const outcome = await tryResolveForAccount(uid, accountId, db, commitState);
          if (outcome === 'resolved') {
            console.log('[useRbac] resolved via accountId:', accountId);
            return;
          }
          if (outcome === 'error') anyReadError = true;
        }
        // No candidate resolved. Distinguish a genuine "not a member of any
        // candidate account" (error:null → legitimate access-restricted) from
        // "we couldn't read the RBAC docs at all" (error:'resolve_failed' →
        // retryable). Without this, a transient/blocked read would silently
        // present a false "access restricted" wall to a real owner.
        console.warn('[useRbac] no candidate resolved to a member doc or owner field', candidates, 'anyReadError=', anyReadError);
        commitState({
          permissions: new Set(),
          isOwner: false,
          loading: false,
          error: anyReadError ? 'resolve_failed' : null,
          usageVisibilityGeneration: null,
          membershipStatus: null,
        });
        return;
      }

      // No Firebase Auth session — fall back to Cloud Function with first candidate
      const accountId = candidates[0];
      if (!accountId) {
        commitState({ permissions: new Set(), isOwner: false, loading: false, error: 'no_account', usageVisibilityGeneration: null, membershipStatus: null });
        return;
      }
      try {
        const { getMyPermissions } = await import('../services/rbacService');
        const data = await getMyPermissions(accountId);
        commitState({
          permissions: new Set(data.permissions),
          isOwner: data.isOwner,
          loading: false,
          error: null,
          usageVisibilityGeneration:
            typeof (data as any).usageVisibilityGeneration === 'number'
              ? (data as any).usageVisibilityGeneration
              : null,
          membershipStatus:
            typeof (data as any).membershipStatus === 'string' ? (data as any).membershipStatus : null,
        });
      } catch (err: any) {
        const code = (err as any)?.code ?? '';
        if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
          commitState({ permissions: new Set(), isOwner: false, loading: false, error: 'session_expired', usageVisibilityGeneration: null, membershipStatus: null });
        } else {
          commitState({ permissions: new Set(), isOwner: false, loading: false, error: err?.message ?? 'failed', usageVisibilityGeneration: null, membershipStatus: null });
        }
      }
      return;
    } catch (err: any) {
      commitState({ permissions: new Set(), isOwner: false, loading: false, error: err?.message ?? 'failed', usageVisibilityGeneration: null, membershipStatus: null });
      return;
    }
  }, [tryResolveForAccount, commit]);

  // Start (or join) the fetch for a key. Concurrent mounts share one walk;
  // subscribers apply the shared outcome when it settles.
  const startFetch = useCallback(
    (key: string, uid: string, accountIdHint: string | undefined, opts?: { force?: boolean }) => {
      let promise = inflightFetches.get(key);
      if (!promise || opts?.force) {
        promise = runFetch(key, uid, accountIdHint).finally(() => {
          if (inflightFetches.get(key) === promise) inflightFetches.delete(key);
        });
        inflightFetches.set(key, promise);
      } else {
        // Join the in-flight walk: mirror its terminal state into this
        // instance (error states included — walls/retry behave as today).
        void promise.then(() => {
          const cached = rbacCache.get(key);
          if (cached) safeSetState(cached.state);
        });
      }
      return promise;
    },
    [runFetch, safeSetState]
  );

  useEffect(() => {
    if (!user?.id) {
      setState(EMPTY_STATE);
      lastFetchedKey.current = null;
      return;
    }
    const fetchKey = cacheKey(user.id, user.accountId);
    const cached = rbacCache.get(fetchKey);

    if (cached?.good) {
      if (lastFetchedKey.current !== fetchKey) {
        lastFetchedKey.current = fetchKey;
        setState(cached.state);
      }
      // Fresh enough — no read at all. Stale — refresh in the background
      // WITHOUT re-entering loading (the resolved state stays on screen).
      if (Date.now() - cached.fetchedAtMs >= RBAC_CACHE_TTL_MS) {
        startFetch(fetchKey, user.id, user.accountId ?? undefined);
      }
      return;
    }

    if (lastFetchedKey.current === fetchKey && inflightFetches.has(fetchKey)) return;
    lastFetchedKey.current = fetchKey;
    setState(prev => ({ ...prev, loading: true }));
    startFetch(fetchKey, user.id, user.accountId ?? undefined);
  }, [user?.id, user?.accountId, startFetch]);

  const hasPermission = useCallback(
    (perm: string) => state.isOwner || state.permissions.has(perm),
    [state.permissions, state.isOwner]
  );

  const refetch = useCallback(() => {
    if (!user?.id) return;
    const fetchKey = cacheKey(user.id, user.accountId);
    rbacCache.delete(fetchKey);
    lastFetchedKey.current = fetchKey;
    setState(prev => ({ ...prev, loading: true }));
    startFetch(fetchKey, user.id, user.accountId ?? undefined, { force: true });
  }, [user?.id, user?.accountId, startFetch]);

  return {
    permissions: state.permissions,
    isOwner: state.isOwner,
    loading: state.loading,
    error: state.error,
    usageVisibilityGeneration: state.usageVisibilityGeneration,
    membershipStatus: state.membershipStatus,
    /**
     * Is there an authenticated identity to enforce against at all?
     *
     * W5 stage 3: the visibility gates now ENFORCE once RBAC has resolved, so
     * they must be able to tell "resolved member who lacks the key" (restrict)
     * from "no user / no AuthProvider" (nothing to enforce — see the
     * EMPTY_STATE note at the top of this hook: embedded hosts and isolated
     * component tests mount outside the provider and must keep failing OPEN).
     * Without this, EMPTY_STATE — loading:false, error:null, no permissions —
     * would read as a permission-less member and hide everything.
     */
    hasIdentity: Boolean(user?.id),
    hasPermission,
    refetch,
  };
}
